import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";

const faqGroups = [
  {
    group: "阅读与使用",
    icon: "ri-book-open-line",
    items: [
      {
        q: "吹雪漫画网可以免费在线阅读吗？",
        a: "可以。吹雪漫画网为读者提供免费的在线阅读服务，本站收录的雪景、冬日、雪国、冰雪、冬物语与雪女等题材作品均可免费观看，无需下载客户端，点开即读。",
      },
      {
        q: "为什么有的作品显示「连载中」？",
        a: "「连载中」表示该作品仍在持续更新，编辑部会根据原作者更新节奏同步最新章节，方便你第一时间追更；「已完结」则表示全部章节均已上架，可放心阅读。",
      },
      {
        q: "漫画画质如何？会不会很模糊？",
        a: "本站统一采用高清原图渲染，点开即读。若在移动端遇到加载较慢的情况，建议切换到稳定网络，或稍作等待后刷新即可获得更清晰的阅读体验。",
      },
      {
        q: "在哪里可以查看每日更新？",
        a: "首页「本周更新」板块会汇总最近更新的作品与章节，你也可以通过顶部搜索或分类页按题材查找感兴趣的冬日漫画。",
      },
    ],
  },
  {
    group: "作品与分类",
    icon: "ri-price-tag-3-line",
    items: [
      {
        q: "有哪些冬日题材分类？",
        a: "我们设有雪景漫画、冬日漫画、雪国漫画、冬物语、冰雪漫画、雪女漫画六大分类，从写实雪景到奇幻雪女传说均有收录，可按喜好进入分类页浏览。",
      },
      {
        q: "想看的作品找不到怎么办？",
        a: "可以先尝试在顶部搜索框输入作品名或关键词；若仍未找到，欢迎通过联系我们页面的表单告诉我们，编辑部会尽快评估收录。",
      },
      {
        q: "作品的评分和人气是怎么来的？",
        a: "评分综合读者反馈与编辑部审读得出，人气根据阅读热度动态更新，用于帮助你更快找到口碑好的冬日佳作。",
      },
    ],
  },
  {
    group: "更新与收藏",
    icon: "ri-refresh-line",
    items: [
      {
        q: "如何把喜欢的作品加入收藏？",
        a: "进入作品详情页后，点击「加入收藏」按钮即可收藏，方便日后快速回到阅读进度。目前收藏仅保存在当前设备，后续会逐步开放跨端同步。",
      },
      {
        q: "「连载中」的作品多久更新一次？",
        a: "更新频率随各作品原作者创作节奏而定，通常在连载稳定后每周更新。首页「本周更新」会第一时间汇总最新章节。",
      },
    ],
  },
];

export default function Faq() {
  const [open, setOpen] = useState<string>("阅读与使用-0");

  const faqList = faqGroups.flatMap((g) => g.items);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const toggle = (group: string, idx: number) => {
    const key = `${group}-${idx}`;
    setOpen((v) => (v === key ? "" : key));
  };

  return (
    <Layout>
      <Seo
        title="常见问题 - 吹雪漫画网"
        description="吹雪漫画网常见问题解答：在线阅读、连载状态、画质、更新、分类与收藏等相关疑问，一次说清，阅读无忧。"
        keywords="吹雪漫画网,常见问题,雪景漫画,冬日漫画,雪国漫画,冬物语,冰雪漫画,雪女漫画"
        canonical="https://secretom.github.io/faq"
        jsonLd={[jsonLd]}
      />

      <section className="relative flex min-h-[280px] w-full items-center overflow-hidden pt-24 md:min-h-[330px]">
        <div className="absolute inset-0 bg-background-100"></div>
        <div className="absolute -right-20 top-6 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 text-center md:px-6">
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-accent-500/40 bg-accent-500/10 px-4 py-1.5 text-sm text-accent-500">
            <i className="ri-question-line"></i> 常见问题
          </span>
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
            阅读前的疑问，一次说清
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-base">
            关于在线阅读、作品分类、更新与收藏的问题，都可以在这里找到答案。若仍有疑问，欢迎联系我们。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
        {faqGroups.map((g) => (
          <div key={g.group} className="mb-8 last:mb-0">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className={`${g.icon} text-primary-500`}></i> {g.group}
            </h2>
            <div className="space-y-3">
              {g.items.map((f, i) => {
                const key = `${g.group}-${i}`;
                const isOpen = open === key;
                return (
                  <div
                    key={f.q}
                    className={`overflow-hidden rounded-lg border transition-colors ${
                      isOpen ? "border-primary-500/40 bg-background-100" : "border-background-200/60 bg-background-100"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(g.group, i)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                    >
                      <span className="font-heading text-base font-semibold text-foreground-950">{f.q}</span>
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-transform ${isOpen ? "rotate-180 bg-primary-500 text-background-950" : "bg-secondary-100 text-foreground-700"}`}>
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </button>
                    {isOpen && (
                      <p className="border-t border-background-200/60 px-5 py-4 text-sm leading-7 text-foreground-600">{f.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-lg border border-background-200/60 bg-background-100 p-6 sm:flex-row">
          <p className="text-sm text-foreground-600">还有没解决的问题？随时来信告诉我们。</p>
          <Link to="/contact" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600">
            联系我们 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </Layout>
  );
}